import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Building2,
  FileKey,
  FileText,
  Award,
  Loader2,
  Mail,
  Calculator,
} from "lucide-react";
import {
  useTaxAuthorityConfig,
  useFolioAuthorizations,
  useVerifySiiConnection,
  SiiConfigForm,
  CertificateUpload,
  ConnectionStatus,
  FolioList,
  CertificationProgress,
  GmailConnection,
  AccountingConfigForm,
  SiiWebCredentials,
  type SiiEmisor,
  type AccountingConfig,
} from "@/features/tax-authority";

/**
 * Main page for SII Tax Authority configuration.
 *
 * Organized in tabs:
 * - Empresa: Company info (Emisor context)
 * - Certificado: Certificate upload and connection verification
 * - Folios: CAF management
 * - Certificación: Certification workflow status
 */
export function AutoridadTributariaPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "empresa";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update tab when URL changes (e.g., OAuth callback)
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam &&
      [
        "empresa",
        "certificado",
        "folios",
        "certificacion",
        "recepcion",
        "contabilidad",
      ].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const {
    config,
    isLoading: isLoadingConfig,
    updateConfig,
    isUpdating,
  } = useTaxAuthorityConfig();

  const {
    folios,
    isLoading: isLoadingFolios,
    uploadCaf,
    isUploading,
    deactivateFolio,
  } = useFolioAuthorizations();

  const { verify, isVerifying, lastResult } = useVerifySiiConnection();

  // Handlers
  const handleSaveEmisor = async (emisor: SiiEmisor) => {
    try {
      await updateConfig({ emisor });
      toast.success("Información de empresa guardada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al guardar");
      throw err;
    }
  };

  const handleUploadCertificate = async (
    certificateP12: string,
    certificatePassword: string,
  ) => {
    try {
      await updateConfig({ certificateP12, certificatePassword });
      toast.success("Certificado cargado exitosamente");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al cargar certificado",
      );
      throw err;
    }
  };

  const handleSaveSiiWebCredentials = async (
    siiWebRut: string,
    siiWebPassword: string,
  ) => {
    try {
      await updateConfig({ siiWebRut, siiWebPassword });
      toast.success("Credenciales web SII guardadas");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar credenciales",
      );
      throw err;
    }
  };

  const handleVerifyConnection = async () => {
    try {
      const result = await verify();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      return result;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al verificar conexión",
      );
      throw err;
    }
  };

  const handleUploadCaf = async (cafXml: string) => {
    try {
      await uploadCaf(cafXml);
      toast.success("CAF cargado exitosamente");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al cargar CAF");
      throw err;
    }
  };

  const handleDeactivateFolio = async (id: string) => {
    try {
      await deactivateFolio(id);
      toast.success("Folio desactivado");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al desactivar folio",
      );
      throw err;
    }
  };

  const handleSaveAccountingConfig = async (
    accountingConfig: AccountingConfig,
  ) => {
    try {
      await updateConfig({ accountingConfig });
      toast.success("Configuración contable guardada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al guardar");
      throw err;
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const environment = config?.environment ?? "certification";
  const hasCertificate = config?.hasCertificate ?? false;

  return (
    <div className="container max-w-4xl py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Configuración SII</h1>
        <p className="text-muted-foreground">
          Configure su integración con el Servicio de Impuestos Internos
        </p>
      </div>

      {/* Environment Badge */}
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            environment === "production"
              ? "bg-green-100 text-green-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {environment === "production" ? "Producción" : "Certificación"}
        </span>
        {hasCertificate && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Certificado configurado
          </span>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="empresa" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Empresa</span>
          </TabsTrigger>
          <TabsTrigger value="certificado" className="flex items-center gap-2">
            <FileKey className="h-4 w-4" />
            <span className="hidden sm:inline">Certificado</span>
          </TabsTrigger>
          <TabsTrigger value="folios" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Folios</span>
          </TabsTrigger>
          <TabsTrigger value="recepcion" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Recepcion</span>
          </TabsTrigger>
          <TabsTrigger value="contabilidad" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            <span className="hidden sm:inline">Contabilidad</span>
          </TabsTrigger>
          <TabsTrigger
            value="certificacion"
            className="flex items-center gap-2"
          >
            <Award className="h-4 w-4" />
            <span className="hidden sm:inline">Certificacion</span>
          </TabsTrigger>
        </TabsList>

        {/* Empresa Tab */}
        <TabsContent value="empresa" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Información de la Empresa</CardTitle>
              <CardDescription>
                Datos del emisor utilizados en los documentos tributarios
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SiiConfigForm
                emisor={config?.emisor ?? null}
                onSave={handleSaveEmisor}
                isLoading={isUpdating}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certificado Tab */}
        <TabsContent value="certificado" className="mt-6 space-y-6">
          <CertificateUpload
            hasCertificate={hasCertificate}
            certificateExpiry={config?.certificateExpiry}
            onUpload={handleUploadCertificate}
            isUploading={isUpdating}
          />

          <SiiWebCredentials
            hasSiiWebCredentials={config?.hasSiiWebCredentials ?? false}
            onSave={handleSaveSiiWebCredentials}
            isSaving={isUpdating}
          />

          <ConnectionStatus
            hasCertificate={hasCertificate}
            environment={environment}
            onVerify={handleVerifyConnection}
            isVerifying={isVerifying}
            lastResult={lastResult}
          />
        </TabsContent>

        {/* Folios Tab */}
        <TabsContent value="folios" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Gestión de Folios</CardTitle>
              <CardDescription>
                Administre sus autorizaciones de folios (CAF) para cada tipo de
                documento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FolioList
                folios={folios}
                isLoading={isLoadingFolios}
                onUpload={handleUploadCaf}
                isUploading={isUploading}
                onDeactivate={handleDeactivateFolio}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recepcion Tab */}
        <TabsContent value="recepcion" className="mt-6">
          <GmailConnection />
        </TabsContent>

        {/* Contabilidad Tab */}
        <TabsContent value="contabilidad" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Integración Contable</CardTitle>
              <CardDescription>
                Configure las cuentas contables para la centralización de
                compras y ventas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AccountingConfigForm
                config={config?.accountingConfig}
                onSave={handleSaveAccountingConfig}
                isLoading={isUpdating}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certificacion Tab */}
        <TabsContent value="certificacion" className="mt-6">
          <CertificationProgress
            certification={config?.certification}
            environment={environment}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
